/**
 * InfoRequestModal - "Need More Information" Editor
 * 
 * Shown when AI returns low confidence or missing data.
 * Allows user to manually correct/add information before resubmit.
 * 
 * Whiteboard: "Enhance Congratulation popup / Need more information"
 */

import React, { useState, useEffect } from 'react';
import AdminEditLeadModal from './admin/AdminEditLeadModal';
import { BACKEND_URL } from '../utils/constants';

const InfoRequestModal = ({ 
  isVisible, 
  aiData = {}, 
  imageData = null,
  onCancel, 
  onResubmit,
  onEditComplete  // 🆕 Called after edit modal saves
}) => {
  // 🆕 State for Edit Submission modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    serialNumber: '',
    businessName: '',
    address: '',
    contactName: '',
    contactPhone: '',
    notes: ''
  });

  // Auto-populate with AI-extracted data (if any)
  useEffect(() => {
    if (isVisible && aiData) {
      setFormData({
        serialNumber: aiData.serial_number || '',
        businessName: aiData.business_name || '',
        address: aiData.address || '',
        contactName: aiData.contact_name || '',
        contactPhone: aiData.contact_phone || '',
        notes: aiData.notes || ''
      });
    }
  }, [isVisible, aiData]);

  if (!isVisible) return null;

  // 🆕 Build inspection object for Edit modal
  const inspectionForEdit = aiData.inspection || {
    id: aiData.inspection_id,
    business_name: formData.businessName,
    location: formData.address,
    notes: formData.notes,
    serial_number: formData.serialNumber
  };

  const handleSubmit = () => {
    // Pass corrected data back to parent for resubmission
    if (onResubmit) {
      onResubmit({
        ...formData,
        corrected_by_user: true,
        original_ai_confidence: aiData.confidence_score || 0
      });
    }
  };

  // Identify which fields are missing or low confidence
  const missingFields = [];
  if (!formData.serialNumber || formData.serialNumber.trim() === '') {
    missingFields.push('Serial Number');
  }
  if (!formData.businessName || formData.businessName.trim() === '') {
    missingFields.push('Business Name');
  }

  const confidenceLevel = aiData.confidence_score || 0;
  const isLowConfidence = confidenceLevel < 0.8;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full relative shadow-2xl max-h-[90vh] overflow-y-auto">
        
        {/* Header - Attention Required */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <span className="text-5xl">📝</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-[var(--pbfs-navy)] mb-2 text-center">
          Need More Information
        </h2>
        
        <p className="text-gray-600 text-center mb-6">
          {isLowConfidence 
            ? `AI confidence: ${Math.round(confidenceLevel * 100)}%. Please verify the details below.`
            : 'Please fill in the missing information to complete your submission.'}
        </p>

        {/* Preview of captured image */}
        {imageData && (
          <div className="mb-4">
            <img 
              src={imageData} 
              alt="Captured tag" 
              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
            />
          </div>
        )}

        {/* Missing Fields Alert */}
        {missingFields.length > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm font-medium text-yellow-800">
              ⚠️ Missing: {missingFields.join(', ')}
            </p>
          </div>
        )}

        {/* Editable Form Fields */}
        <div className="space-y-4">
          {/* Serial Number - CRITICAL */}
          <div>
            <label className="block text-sm font-bold text-[var(--pbfs-navy)] mb-1">
              Serial Number {!formData.serialNumber && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={formData.serialNumber}
              onChange={(e) => setFormData({...formData, serialNumber: e.target.value})}
              placeholder="Enter serial number from tag"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[var(--pbfs-green)] focus:outline-none text-lg font-mono"
              autoFocus
            />
          </div>

          {/* Business Name */}
          <div>
            <label className="block text-sm font-bold text-[var(--pbfs-navy)] mb-1">
              Business Name {!formData.businessName && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={formData.businessName}
              onChange={(e) => setFormData({...formData, businessName: e.target.value})}
              placeholder="Business or facility name"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[var(--pbfs-green)] focus:outline-none"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-bold text-[var(--pbfs-navy)] mb-1">
              Address (Optional)
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Street address"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[var(--pbfs-green)] focus:outline-none"
            />
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-bold text-[var(--pbfs-navy)] mb-1">
              Additional Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional context or corrections..."
              rows="3"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[var(--pbfs-green)] focus:outline-none resize-none"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3 mt-6">
          {/* Primary: Resubmit with Corrections */}
          <button
            onClick={handleSubmit}
            disabled={!formData.serialNumber || !formData.businessName}
            className={`w-full font-bold py-4 px-6 rounded-lg transition-all duration-300 shadow-lg text-lg ${
              formData.serialNumber && formData.businessName
                ? 'bg-[var(--pbfs-green)] hover:bg-opacity-90 text-white transform hover:scale-105'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            ✅ Resubmit with Corrections
          </button>

          {/* 🆕 Secondary: Edit Submission (opens full edit modal) */}
          {aiData.inspection_id && (
            <button
              onClick={() => setShowEditModal(true)}
              className="w-full bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-navy)] text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              ✏️ Edit Submission Details
            </button>
          )}

          {/* Tertiary: Continue (skip corrections) */}
          <button
            onClick={onCancel}
            className="w-full bg-white hover:bg-gray-50 border-2 border-gray-300 text-[var(--pbfs-navy)] font-medium py-3 px-6 rounded-lg transition-all duration-300"
          >
            📷 Continue Scanning
          </button>
        </div>

        {/* Help Text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          💡 Tip: Double-check the serial number - it's the most important field for tracking!
        </p>
      </div>

      {/* 🆕 Edit Submission Modal (same as from Leaderboard) */}
      <AdminEditLeadModal
        isOpen={showEditModal}
        inspection={inspectionForEdit}
        backendUrl={BACKEND_URL}
        onClose={() => setShowEditModal(false)}
        onSaved={(updated) => {
          setShowEditModal(false);
          // Close InfoRequestModal and notify parent
          if (onEditComplete) {
            onEditComplete(updated);
          } else if (onCancel) {
            onCancel(); // Fall back to closing the modal
          }
        }}
      />
    </div>
  );
};

export default InfoRequestModal;
